import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronRight, 
  faQuestionCircle, 
  faLightbulb,
  faComments,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../componentes/PageHeader';
import { FullscreenSpinner } from '../../componentes/LoadingIndicator';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchFAQs } from '../../api/faq';
import MetaTags from '../../componentes/MetaTags';

const Faq = () => {
  const { t, i18n } = useTranslation();
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const backgroundIcons = [faQuestionCircle, faLightbulb, faComments];

  useEffect(() => {
    const loadFAQs = async () => {
      try {
        setLoading(true);
        const response = await fetchFAQs({
          page: currentPage,
          lang: i18n.language
        });
        setFaqs(response.results);
        setActiveQuestion(response.results[0].id);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadFAQs();
  }, [currentPage, i18n.language]);

  if (error) {
    return (
      <>
      <PageHeader />
      <div className="text-center py-12">
        <p className="text-red-500">{error}</p>
      </div>
       </>
    );
  }

  return (
    <>
      <MetaTags title="FAQ — Ethio-CERT" description="Frequently asked questions about Ethio-CERT services and reporting." url="https://ethiocert.gov.et/about/faq" />
      <PageHeader />
      <div className="bg-gradient-to-r from-midnigt to-shake pt-24 md:pt-24 px-4 md:px-28 min-h-[14rem] md:min-h-[18rem] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                opacity: 0.1
              }}
            >
              <FontAwesomeIcon 
                icon={backgroundIcons[i % backgroundIcons.length]} 
                className="text-white text-4xl"
              />
            </div>
          ))}
        </div>

        {/* Header content */}
        <div className="relative z-10">
          <div className="flex flex-wrap items-center gap-2 text-white/80 text-sm mb-6 md:mb-12 max-xl:mt-8">
            <Link to="/" className="hover:text-white transition-colors">
              {t("header.home")}
            </Link>
            <span>/</span>
            <span className="text-white">{t("header.faq")}</span>
          </div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-h-5 font-bold text-white mb-8"
          >
            {t("faq.title")}
          </motion.h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16 -mt-10">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <FullscreenSpinner className="py-0" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Questions and Answers List */}
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={faq.id} className="space-y-2">
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`w-full flex items-center font-nunito-sans justify-between p-6 text-left rounded-xl transition-all duration-300 
                      transform hover:scale-105 backdrop-blur-sm
                      ${activeQuestion === faq.id 
                        ? 'bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg shadow-primary/30' 
                        : 'bg-white/90 hover:bg-white shadow-lg hover:shadow-xl'}`}
                    onClick={() => setActiveQuestion(activeQuestion === faq.id ? null : faq.id)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-4 h-4 rounded-full flex-shrink-0 transition-all duration-300
                        ${activeQuestion === faq.id 
                          ? 'bg-white scale-125' 
                          : 'bg-primary scale-100'}`} 
                      />
                      <span className="font-medium text-lg">{faq.translations.question}</span>
                    </div>
                    <FontAwesomeIcon 
                      icon={faChevronRight}
                      className={`transition-transform duration-300 
                        ${activeQuestion === faq.id ? 'rotate-90' : ''}`}
                    />
                  </motion.button>

                  {/* Mobile Answer */}
                  <AnimatePresence>
                    {activeQuestion === faq.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-white rounded-xl p-6 shadow-lg"
                      >
                        <div 
                          className="text-gray-700 leading-relaxed font-nunito-sans"
                          dangerouslySetInnerHTML={{ __html: faq.translations.answer }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* Desktop Answer Display */}
            <div className="hidden lg:block lg:sticky lg:top-24 h-fit">
              <motion.div 
                layout
                className="bg-white rounded-2xl shadow-2xl p-8 backdrop-blur-lg
                  border border-gray-100 overflow-hidden"
              >
                <AnimatePresence mode="wait">
                  {activeQuestion && (
                    <motion.div
                      key={activeQuestion}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h2 className="text-2xl font-bold mb-6 text-gray-800
                        border-b border-gray-200 pb-4 font-nunito-sans">
                        {faqs.find(faq => faq.id === activeQuestion)?.translations.question}
                      </h2>
                      <div 
                        className="text-gray-700 leading-relaxed text-lg"
                        dangerouslySetInnerHTML={{ 
                          __html: faqs.find(faq => faq.id === activeQuestion)?.translations.answer 
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Faq;
